"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

type SiteHeaderProps = {
  absolute?: boolean;
  /** Home: transparent at scroll 0 over the hero; solid bar once the page is scrolled. */
  transparentAtTop?: boolean;
};

/** Solid nav appears after this many pixels of scroll (small value = quick response). */
const SCROLL_SOLID_BG_PX = 4;

const navItems = [
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/demos", label: "Demos" },
  { href: "/#process", label: "Process" },
  { href: "/pricing", label: "Pricing" },
  { href: "/contact", label: "Contact" },
] as const;

function NavLinks({
  className,
  onNavigate,
}: {
  className?: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {navItems.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className={className}
          onClick={onNavigate}
        >
          {item.label}
        </Link>
      ))}
    </>
  );
}

export default function SiteHeader({ absolute = false, transparentAtTop = false }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!transparentAtTop || absolute) return;
    const onScroll = () => {
      setScrolled(window.scrollY > SCROLL_SOLID_BG_PX);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentAtTop, absolute]);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const solidStickyBar = scrolled || menuOpen;

  const headerBg = (() => {
    if (absolute) {
      return "absolute inset-x-0 top-0 z-50 bg-black/50 backdrop-blur-md md:bg-transparent md:backdrop-blur-none";
    }
    if (transparentAtTop) {
      /** Fixed so the bar actually sits over the hero video; sticky would sit above the hero and show main bg. */
      const shell = "fixed inset-x-0 top-0 z-40";
      if (!solidStickyBar) return `${shell} bg-transparent`;
      return `${shell} bg-black/80 backdrop-blur-sm`;
    }
    return "sticky top-0 z-40 bg-black/80 backdrop-blur-sm";
  })();

  const headerTransition =
    !absolute && transparentAtTop
      ? "transition-[background-color,backdrop-filter] duration-300 ease-out"
      : !absolute
        ? "transition-colors duration-200"
        : "";

  return (
    <header className={`${headerBg} ${headerTransition}`.trim()}>
      <nav className="mx-auto w-full max-w-6xl px-6 py-4" aria-label="Main">
        {/* Mobile: logo row + scrollable links under logo */}
        <div className="md:hidden">
          <div className="flex items-center justify-between gap-3">
            <Link href="/" className="inline-flex shrink-0 items-center justify-center overflow-visible">
              <Image
                src="/etechra.webp"
                alt="Etechra"
                width={200}
                height={49}
                sizes="160px"
                className="h-auto w-40 rounded-md object-contain"
                priority
              />
            </Link>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-600 text-zinc-200 hover:border-[#b89a5b]/70 hover:text-[#d4bc8c]"
                aria-expanded={menuOpen}
                aria-controls="site-mobile-nav"
                onClick={() => setMenuOpen((o) => !o)}
              >
                <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
                {menuOpen ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              <a
                href="https://wa.me/923247848538"
                target="_blank"
                rel="noreferrer"
                className="relative z-50 inline-flex items-center gap-1.5 rounded-full bg-[#b89a5b] px-3 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-[#b89a5b]/90 active:bg-[#b89a5b]/80"
              >
                <svg className="h-4 w-4 shrink-0 text-zinc-950" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.52 3.48A11.86 11.86 0 0 0 12.08 0C5.53 0 .2 5.33.2 11.88c0 2.09.55 4.14 1.6 5.95L0 24l6.36-1.67a11.85 11.85 0 0 0 5.72 1.46h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.15-3.45-8.43ZM12.08 21.8h-.01a9.86 9.86 0 0 1-5.02-1.37l-.36-.21-3.78.99 1.01-3.68-.24-.38a9.86 9.86 0 0 1-1.5-5.27c0-5.44 4.43-9.87 9.88-9.87 2.63 0 5.1 1.02 6.96 2.89a9.79 9.79 0 0 1 2.9 6.98c0 5.44-4.43 9.87-9.87 9.87Zm5.41-7.39c-.3-.15-1.78-.88-2.06-.98-.28-.1-.49-.15-.7.15-.21.3-.8.98-.98 1.18-.18.2-.36.22-.66.07-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.51-1.79-1.69-2.09-.18-.3-.02-.47.14-.62.14-.14.3-.36.45-.54.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.7-1.7-.95-2.32-.25-.6-.5-.52-.7-.53l-.6-.01c-.2 0-.52.08-.8.37-.28.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.08 4.48.71.31 1.27.5 1.7.64.72.23 1.37.2 1.89.12.58-.09 1.78-.73 2.03-1.43.25-.7.25-1.31.18-1.43-.07-.12-.27-.2-.57-.35Z" />
                </svg>
                <span className="max-[360px]:hidden">Let&apos;s Talk</span>
              </a>
            </div>
          </div>

          <div
            id="site-mobile-nav"
            className={
              menuOpen
                ? "mt-3 max-h-[min(70vh,26rem)] w-full overflow-y-auto overscroll-y-contain rounded-xl border border-zinc-800 bg-zinc-950/95 py-2 shadow-lg shadow-black/40"
                : "hidden"
            }
            role="navigation"
            aria-label="Sections"
          >
            <NavLinks
              className="block rounded-lg px-4 py-3 text-base font-medium text-zinc-100 transition hover:bg-zinc-800/80 hover:text-[#d4bc8c]"
              onNavigate={() => setMenuOpen(false)}
            />
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden items-center justify-between gap-6 md:flex">
          <Link href="/" className="inline-flex shrink-0 items-center justify-center overflow-visible">
            <Image
              src="/etechra.webp"
              alt="Etechra"
              width={200}
              height={49}
              sizes="160px"
              className="h-auto w-40 rounded-md object-contain"
              loading="eager"
            />
          </Link>
          <div className="flex flex-1 items-center justify-center gap-6 text-sm text-zinc-300">
            <NavLinks className="transition hover:text-[#d4bc8c]" />
          </div>
          <a
            href="https://wa.me/923247848538"
            target="_blank"
            rel="noreferrer"
            className="relative z-50 inline-flex shrink-0 items-center gap-2 rounded-full bg-[#b89a5b] px-4 py-2 text-sm font-semibold text-zinc-950 transition hover:bg-[#b89a5b]/90 active:bg-[#b89a5b]/80"
          >
            <svg className="h-4 w-4 text-zinc-950" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M20.52 3.48A11.86 11.86 0 0 0 12.08 0C5.53 0 .2 5.33.2 11.88c0 2.09.55 4.14 1.6 5.95L0 24l6.36-1.67a11.85 11.85 0 0 0 5.72 1.46h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.15-3.45-8.43ZM12.08 21.8h-.01a9.86 9.86 0 0 1-5.02-1.37l-.36-.21-3.78.99 1.01-3.68-.24-.38a9.86 9.86 0 0 1-1.5-5.27c0-5.44 4.43-9.87 9.88-9.87 2.63 0 5.1 1.02 6.96 2.89a9.79 9.79 0 0 1 2.9 6.98c0 5.44-4.43 9.87-9.87 9.87Zm5.41-7.39c-.3-.15-1.78-.88-2.06-.98-.28-.1-.49-.15-.7.15-.21.3-.8.98-.98 1.18-.18.2-.36.22-.66.07-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.51-1.79-1.69-2.09-.18-.3-.02-.47.14-.62.14-.14.3-.36.45-.54.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.7-1.7-.95-2.32-.25-.6-.5-.52-.7-.53l-.6-.01c-.2 0-.52.08-.8.37-.28.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.08 4.48.71.31 1.27.5 1.7.64.72.23 1.37.2 1.89.12.58-.09 1.78-.73 2.03-1.43.25-.7.25-1.31.18-1.43-.07-.12-.27-.2-.57-.35Z" />
            </svg>
            Let&apos;s Talk
          </a>
        </div>
      </nav>
    </header>
  );
}
